import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

const ExitIntentPopup = ({ config }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Default values if config is not provided
  const heading = config?.heading || "Don't miss out!";
  const description =
    config?.description || "Subscribe to get exclusive updates and offers.";
  const buttonText = config?.buttonText || "Subscribe Now";
  const caption =
    config?.caption ||
    "By subscribing, you agree to our privacy policy and terms of service.";
  const successHeading = config?.successHeading || "Thank You!";
  const successMessage =
    config?.successMessage || "We've received your subscription.";
  const enabled = config?.enable !== undefined ? config.enable : true;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful, isDirty },
    reset,
  } = useForm({
    mode: "onBlur",
  });

  // Function to detect exit intent
  useEffect(() => {
    // Skip if disabled, user already interacted with popup, or submitted the form
    if (!enabled || hasInteracted || isSubmitted) return;

    // Check if we have already shown popup in this session
    const hasSeenPopup = sessionStorage.getItem("hasSeenExitPopup");
    if (hasSeenPopup) return;

    // Handler for mouse leaving the window (top of the page)
    const handleMouseLeave = (e) => {
      // Only trigger when mouse leaves at the top of the page
      if (e.clientY <= 0) {
        setShowPopup(true);
        document.removeEventListener("mouseleave", handleMouseLeave);
        // Mark as seen for this session
        sessionStorage.setItem("hasSeenExitPopup", "true");
      }
    };

    // Add timeout to ensure it doesn't show immediately after page load
    const timer = setTimeout(() => {
      document.addEventListener("mouseleave", handleMouseLeave);
    }, 3000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [enabled, hasInteracted, isSubmitted]);

  // Handle form submission
  const onSubmit = async (data) => {
    const toastId = toast.loading("Submitting...");

    try {
      const response = await fetch(
        "https://leads.civsav.com/template/contact",
        {
          method: "POST",
          body: JSON.stringify({
            data: {
              email: data.email,
              date: new Date().toUTCString(),
              website: window.location.origin,
            },
            sheetID: 57, // Same sheet ID as other forms
          }),
          headers: {
            "Content-type": "application/json; charset=UTF-8",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Submission failed");
      }

      // Track conversion in analytics if available
      if (typeof window !== "undefined" && window.dataLayer) {
        window.dataLayer.push({
          event: "exit_intent_conversion",
          form_type: "exit_intent",
        });
      }

      setIsSubmitted(true);
      reset();
      toast.success("Thank you for subscribing!");
      toast.dismiss(toastId);
      // Close popup after short delay
      setTimeout(() => {
        setShowPopup(false);
      }, 2000);
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Something went wrong. Please try again.");
      toast.dismiss(toastId);
    }
  };

  // Handle closing the popup
  const handleClose = () => {
    setShowPopup(false);
    setHasInteracted(true);
  };

  if (!showPopup || !enabled) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm transition-opacity duration-300">
      <div className="relative w-full max-w-lg bg-white rounded-lg shadow-2xl overflow-hidden mx-4 transform transition-all duration-300 opacity-100 translate-y-0 scale-100">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors duration-200"
          aria-label="Close popup"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="p-8">
          {isSubmitted ? (
            <div className="text-center py-8">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 text-primary mx-auto"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <h2 className="text-2xl font-bold mt-4 mb-2">{successHeading}</h2>
              <p className="text-black">{successMessage}</p>
            </div>
          ) : (
            <>
              <h2 className="text-3xl font-bold mb-4 text-center">{heading}</h2>
              <p className="text-black mb-6 text-center">{description}</p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <input
                    type="email"
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="Enter your email"
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    })}
                  />
                  {errors.email && (
                    <p className="mt-1 text-red-500 text-sm">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full main-btn btn-yellow py-3 px-4 text-white font-medium rounded-md transition-colors duration-200"
                >
                  {buttonText}
                </button>

                {caption && (
                  <p className="text-xs text-gray-500 text-center mt-4">
                    {caption}
                  </p>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
